import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import api from '../api';

function HeaderUserSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search users when debounced query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      searchUsers(debouncedQuery);
    } else {
      setSearchResults([]);
      setIsOpen(false);
    }
  }, [debouncedQuery]);

  const searchUsers = async (query) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/users/search?query=${query}&limit=8`);
      
      // Handle both possible response structures
      let users = [];
      if (Array.isArray(response.data)) {
        users = response.data;
      } else if (response.data?.users) {
        users = response.data.users;
      }
      
      setSearchResults(users);
      setIsOpen(users.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSearchQuery('');
    setSearchResults([]);
    setIsOpen(false);
    navigate(`/user/${user.id}/details`);
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setIsOpen(true);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const formatMoani = (moani) => {
    if (!moani || moani === '0') return '0';
    // Convert from wei to MOANI (assuming 18 decimals)
    const moaniValue = parseFloat(moani) / Math.pow(10, 18);
    return moaniValue.toFixed(2);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: 280 }}>
        <TextField
          ref={anchorRef}
          size="small"
          variant="outlined"
          placeholder="Search users..."
          value={searchQuery}
          onChange={handleInputChange}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255, 255, 255, 0.7)',
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                ) : (
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                )}
              </InputAdornment>
            ),
          }}
        />

        <Popper
          open={isOpen && searchResults.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 9999, width: anchorRef.current?.offsetWidth || 280 }}
        >
          <Paper
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 1,
              mt: 0.5,
            }}
          >
            <List sx={{ py: 0 }}>
              {searchResults.map((user) => (
                <ListItem key={user.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleUserSelect(user)}
                    sx={{
                      py: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(144, 202, 249, 0.08)',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          background: 'linear-gradient(135deg, #00e5ff 0%, #2196f3 100%)',
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {user.name || 'Unnamed User'}
                          </Typography>
                          {/* Status indicators */}
                          {(user.banned && user.banned !== 0) && (
                            <Chip label="BANNED" color="error" size="small" sx={{ fontSize: '0.6rem', height: 16 }} />
                          )}
                          {(user.cheater && user.cheater !== 0) && (
                            <Chip label="CHEATER" color="warning" size="small" sx={{ fontSize: '0.6rem', height: 16 }} />
                          )}
                          {user.permaban && (
                            <Chip label="PERMABAN" color="error" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: 'error.dark' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {user.email}
                          </Typography>
                          {user.wallet && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {user.wallet.substring(0, 6)}...{user.wallet.substring(user.wallet.length - 4)}
                            </Typography>
                          )}
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#00e676',
                              fontWeight: 'bold',
                              display: 'block'
                            }}
                          >
                            MOANI: {formatMoani(user.moani)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default HeaderUserSearch;
